import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { api } from "@/services/api";
import { Plus, Search, Copy, FileText, Eye, X, Loader2, AlertTriangle } from "lucide-react";

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const badgeColor = {
  paid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  overdue: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  cancelled: "bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-300",
};

export default function InvoicesPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [orgId, setOrgId] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 15;
  const [cloneInvoice, setCloneInvoice] = useState(null);
  const [cloneOrgId, setCloneOrgId] = useState("");
  const [cancellingInvoiceId, setCancellingInvoiceId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);

  const { data: orgs = [] } = useQuery({ queryKey: ["orgs"], queryFn: async () => (await api.get("/organizations")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["invoices", { q, orgId, status, page }],
    queryFn: async () => (await api.get("/invoices", { params: { q: q || undefined, organization_id: orgId || undefined, status: status || undefined, page, page_size: pageSize } })).data,
  });

  const handleRecordPayment = (invoice) => {
    nav(`/app/payments/new`, { state: { invoiceId: invoice.id, customerId: invoice.customer_id } });
  };

  const duplicate = useMutation({
    mutationFn: async ({ inv, targetOrgId }) => {
      // Convert absolute tax amounts to percentages for cloning
      const clonedSubtotal = inv.subtotal || 0;
      const clonedDiscount = inv.discount || 0;
      const taxableAmount = clonedSubtotal - clonedDiscount;
      
      const cgstPercent = taxableAmount > 0 ? Math.round(((inv.cgst / taxableAmount) * 100) * 100) / 100 : 0;
      const sgstPercent = taxableAmount > 0 ? Math.round(((inv.sgst / taxableAmount) * 100) * 100) / 100 : 0;
      const igstPercent = taxableAmount > 0 ? Math.round(((inv.igst / taxableAmount) * 100) * 100) / 100 : 0;
      
      // Convert discount from absolute to percentage
      const discountPercent = clonedSubtotal > 0 ? Math.round(((clonedDiscount / clonedSubtotal) * 100) * 100) / 100 : 0;
      
      // Navigate to new invoice page with clone data
      nav(`/app/invoices/new`, {
        state: {
          cloneData: {
            ...inv,
            organization_id: targetOrgId,
            discount: discountPercent,
            cgst: cgstPercent,
            sgst: sgstPercent,
            igst: igstPercent,
            roundoff: inv.roundoff || 0,
            items: inv.items?.map(item => ({
              ...item,
              inventory_item_id: item.inventory_item_id || "",
            })) || [],
          },
        },
      });
    },
    onSuccess: () => {
      setCloneInvoice(null);
      setCloneOrgId("");
      toast.success("Opening cloned invoice...");
    },
  });

  const cancelInvoice = useMutation({
    mutationFn: async (invoiceId) => {
      return (await api.put(`/invoices/${invoiceId}/cancel`)).data;
    },
    onMutate: (invoiceId) => {
      setCancellingInvoiceId(invoiceId);
      toast.loading("Cancelling invoice...", { id: "cancel-invoice" });
    },
    onSuccess: () => {
      toast.dismiss("cancel-invoice");
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Invoice cancelled successfully");
      setCancellingInvoiceId(null);
    },
    onError: (e) => {
      toast.dismiss("cancel-invoice");
      toast.error(e?.response?.data?.detail || e.message || "Failed to cancel invoice");
      setCancellingInvoiceId(null);
    },
  });

  const rows = data?.items || [];
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / pageSize));

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em] text-slate-500 mb-2">Invoices</div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tighter">
            All invoices
          </h1>
        </div>
        <Link
          to="/app/invoices/new"
          data-testid="create-invoice-link"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2.5 sm:px-5 sm:py-3 rounded-sm hover:bg-[#001F73] font-semibold self-start md:self-auto text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" /> New Invoice
        </Link>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 border border-slate-300 dark:border-slate-700 px-3 py-2 rounded-sm">
          <Search className="w-4 h-4 text-slate-500" />
          <input data-testid="invoice-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search invoice #" className="w-full bg-transparent outline-none text-sm" />
        </div>
        <div className="flex gap-3">
          <select data-testid="invoice-filter-org" value={orgId} onChange={(e) => { setPage(1); setOrgId(e.target.value); }} className="flex-1 md:flex-none border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm">
            <option value="">All organizations</option>
            {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
          </select>
          <select data-testid="invoice-filter-status" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="flex-1 md:flex-none border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm text-sm">
            <option value="">All status</option>
            {["pending","paid","overdue","cancelled"].map((s) => (<option key={s}>{s}</option>))}
          </select>
        </div>
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-sm bg-white dark:bg-[#121214] overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 dark:bg-slate-900 text-left uppercase text-[10px] tracking-[0.2em] text-slate-500">
            <tr>
              <th className="p-4">Invoice #</th>
              <th className="p-4 hidden md:table-cell">Organization</th>
              <th className="p-4 hidden md:table-cell">Date</th>
              <th className="p-4 hidden md:table-cell">Status</th>
              <th className="p-4 text-right">Amount</th>
              <th className="p-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>)}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-16 text-center text-slate-500" data-testid="invoices-empty">
                <FileText className="w-6 h-6 mx-auto mb-3 text-slate-400" />
                No invoices found.
              </td></tr>
            )}
            {rows.map((r, i) => (
              <motion.tr
                key={r.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className={`border-t border-slate-200/60 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-900 ${cancellingInvoiceId === r.id ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid={`invoice-row-${r.id}`}
              >
                <td className="p-4">
                  <Link to={`/app/invoices/${r.id}`} className="font-semibold hover:text-[#002FA7]">{r.invoice_number}</Link>
                </td>
                <td className="p-4 hidden md:table-cell text-slate-500">{orgs.find((o) => o.id === r.organization_id)?.organization_name || "—"}</td>
                <td className="p-4 hidden md:table-cell text-slate-500">{r.invoice_date}</td>
                <td className="p-4 hidden md:table-cell">
                  <span className={`px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.15em] ${badgeColor[r.calculated_status || r.status] || 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}>
                    {r.calculated_status || r.status}
                  </span>
                </td>
                <td className="p-4 text-right font-semibold">{fmtInr(r.total)}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Link to={`/app/invoices/${r.id}`} className="p-2 hover:text-[#002FA7]" data-testid={`view-invoice-${r.id}`}><Eye className="w-4 h-4" /></Link>
                    {(r.calculated_status || r.status) !== 'cancelled' && (r.calculated_status || r.status) !== 'paid' && (
                      <button 
                        onClick={() => handleRecordPayment(r)} 
                        className="p-2 hover:text-[#002FA7]" 
                        data-testid={`record-payment-${r.id}`}
                        title="Record Payment"
                        disabled={cancellingInvoiceId === r.id}
                      >
                        <span className="font-bold">₹</span>
                      </button>
                    )}
                    <button 
                      onClick={() => { setCloneInvoice(r); setCloneOrgId(r.organization_id); }} 
                      className="p-2 hover:text-[#002FA7]" 
                      data-testid={`clone-invoice-${r.id}`}
                      disabled={cancellingInvoiceId === r.id}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    {(r.calculated_status || r.status) !== 'cancelled' && (r.calculated_status || r.status) !== 'paid' && (
                      <button 
                        onClick={() => {
                          setInvoiceToCancel(r);
                          setShowCancelModal(true);
                        }}
                        className="p-2 hover:text-red-600" 
                        data-testid={`cancel-invoice-${r.id}`}
                        title="Cancel Invoice"
                        disabled={cancellingInvoiceId === r.id}
                      >
                        {cancellingInvoiceId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>Page {page} / {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-sm disabled:opacity-40" data-testid="pagination-prev">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-sm disabled:opacity-40" data-testid="pagination-next">Next</button>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {cloneInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#121214] rounded-sm p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Clone Invoice</h3>
            <p className="text-sm text-slate-500 mb-4">Select organization to clone this invoice to. The invoice number will be auto-generated based on the selected organization.</p>
            <label className="block mb-4">
              <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Target Organization</span>
              <select
                value={cloneOrgId}
                onChange={(e) => setCloneOrgId(e.target.value)}
                className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
              >
                {orgs.map((o) => (<option key={o.id} value={o.id}>{o.organization_name}</option>))}
              </select>
            </label>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setCloneInvoice(null); setCloneOrgId(""); }}
                className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => duplicate.mutate({ inv: cloneInvoice, targetOrgId: cloneOrgId })}
                disabled={duplicate.isPending}
                className="px-4 py-2 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50"
                data-testid="confirm-clone-button"
              >
                {duplicate.isPending ? "Cloning..." : "Clone Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      <AnimatePresence>
        {showCancelModal && invoiceToCancel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-[#121214] rounded-sm p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-500" />
                </div>
                <h3 className="text-lg font-semibold">Cancel Invoice</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">
                Are you sure you want to cancel invoice <span className="font-semibold">{invoiceToCancel.invoice_number}</span>? 
                This action will:
              </p>
              <ul className="text-sm text-slate-600 dark:text-slate-400 mb-4 space-y-1">
                <li>• Set invoice status to cancelled</li>
                <li>• Reverse any payment allocations</li>
                <li>• Restore inventory quantities</li>
                <li>• Update ledger entries</li>
              </ul>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setInvoiceToCancel(null);
                  }}
                  className="px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  No, Keep Invoice
                </button>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    cancelInvoice.mutate(invoiceToCancel.id);
                    setInvoiceToCancel(null);
                  }}
                  disabled={cancelInvoice.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {cancelInvoice.isPending ? "Cancelling..." : "Yes, Cancel Invoice"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
